import { GridColDef } from "@mui/x-data-grid";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { motion } from "motion/react";
import { MutableRefObject, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import CommonGridTable from "@/components/CommonGridTable";
import orderColumn from "@/components/table/orderColumn";
import useWindowDimensions from "@/hooks/useWindowDimensions";
import { BrandColumn } from "@/pages/Vehicles/VehicleTable/columns/BrandColumn";
import { DriverColumn } from "@/pages/Vehicles/VehicleTable/columns/DriverColumn";
import { PlateColumn } from "@/pages/Vehicles/VehicleTable/columns/PlateColumn";
import { useUsersTableIsFullScreen } from "@/stores/use-users-table";
import { OperationServiceFlightInfo } from "@/types/providers";
import { User } from "@/types/user.types";

import { UserActionsColumn } from "./components/UserActionsColumn";
import UserCommonUpdateDialog from "./components/UserCommonUpdateDialog";
import UserCreateComp from "./components/UserCreateComp";
import UserDeleteDialog from "./components/UserDeleteDialog";
import { UserEmailColumn } from "./components/UserEmailColumn";
import { UserIdentityNoColumn } from "./components/UserIdentityNoColumn";
import UserLifeCycleColumn from "./components/UserLifeCycleColumn";
import UserLoginStatusColumn from "./components/UserLoginStatusColumn";
import { UserNameColumn } from "./components/UserNameColumn";
import UserShiftTypeColumn from "./components/UserShiftTypeColumn";
import { UserStatusFilter } from "./components/UserStatusFilter";
import UserToolbar from "./components/UserToolbar";
import UserTypeColumn from "./components/UserTypeColumn";
import UserUpdateShiftDialog from "./components/UserUpdateShiftDialog";
import UserUpdatedAtColumn from "./components/UserUpdatedAtColumn";
import UserVisibilityColumn from "./components/UserVisibilityColumn";
import UserLifecycleDialog from "./components/userLifecycleDialog";
import UserResetPasswordComp from "./components/userResetPassword";
import UserRestoreDialog from "./components/userRestore";
import UpdateUserLifeCycleComp from "./components/userUpdateLifeCycleDialog";
import UpdateUserTypeComp from "./components/userUpdateUserType";
import UserUpdateVehicleComp from "./components/userUpdateVehicle";

function UsersTable({
    rows,
    loading,
    apiRef,
    allRows,
    refetch,
}: {
    rows: OperationServiceFlightInfo<string>[];
    loading: boolean;
    apiRef: MutableRefObject<GridApiPremium>;
    refetch: () => void;
    allRows: OperationServiceFlightInfo<string>[];
    // TODO: `pagePermissions` passed through props but unused in the component
    // TODO: Either use it or remove the prop
    pagePermissions: { edit: boolean };
}) {
    const { t } = useTranslation();
    const { size } = useWindowDimensions();

    const isFullScreen = useUsersTableIsFullScreen();
    const getBrand = useCallback((row: User) => {
        return row.vehicleData?.brand || "";
    }, []);
    const getPlate = useCallback((row: User) => {
        return row.vehicleData?.licensePlate || "";
    }, []);
    const columns: GridColDef[] = useMemo<GridColDef[]>(
        () => [
            orderColumn(),
            DriverColumn({ avatar: true }),
            UserNameColumn(),
            UserEmailColumn(),
            UserIdentityNoColumn(),
            UserTypeColumn({ t }),
            BrandColumn<User>({
                getBrand,
            }),
            PlateColumn<User>({ getPlate }),
            UserVisibilityColumn({ t }),
            UserLoginStatusColumn({ t }),
            UserShiftTypeColumn({ t }),
            UserLifeCycleColumn({ t }),
            UserUpdatedAtColumn({ t }),
            UserActionsColumn(),
        ],
        [size, rows, getPlate, getBrand, t]
    );

    return (
        <>
            <div className="col-span-12 flex w-full grow    overflow-auto  h-full  ">
                <motion.div
                    initial={{ height: "100%", position: "static", width: "100%" }}
                    animate={{
                        height: isFullScreen ? "100vh" : "100%",
                        width: isFullScreen ? "100vw" : "100%",
                        position: isFullScreen ? "fixed" : "static",
                        padding: isFullScreen ? (size.md ? 16 : 4) : 0,
                        top: 0,
                        left: 0,
                        background: isFullScreen ? "white" : "transparent",
                        zIndex: isFullScreen ? 1000 : "auto",
                    }}
                    transition={{ duration: 0.3 }}
                >
                    <CommonGridTable
                        toolbar={() => <UserToolbar reloadTable={refetch} />}
                        rowSelectionModel={[]}
                        loading={loading}
                        rows={rows}
                        columns={columns}
                        api={apiRef}
                        allRows={allRows}
                        initialState={{
                            sorting: {
                                sortModel: [{ field: "userType", sort: "asc" }],
                            },
                            pinnedColumns: {
                                left: ["index", "name"],
                                right: ["actions"],
                            },
                        }}
                        startElement={UserStatusFilter}
                    />
                </motion.div>
            </div>
            <UserDeleteDialog />
            <UserCreateComp />
            <UserCommonUpdateDialog />
            <UserUpdateVehicleComp />
            <UserResetPasswordComp />
            <UpdateUserTypeComp />
            <UserUpdateShiftDialog />
            <UpdateUserLifeCycleComp />
            <UserRestoreDialog />
            <UserLifecycleDialog />
        </>
    );
}

export default UsersTable;
